import { useEffect, useRef, useState } from "react";
import "../../../css/Checks/Check.css";
import "../../../css/Buttons/RadioButton.css";

import SecondaryButton from "../../Components/Buttons/SecondaryButton";
import Banner from "../../Components/Banners/Banner";
import SubmissionPopUp from "../../Components/PopUp/SubmissionPopUp";

import { ReactComponent as PersonalInformationIcon } from "../../../assets/icons/personalInformation.svg";
import { ReactComponent as AddressIcon } from "../../../assets/icons/currentAddress.svg";

import TextInput from "../../Components/Inputs/TextInput";
import DateInput from "../../Components/Inputs/DateInput";
import SearchInput from "../../Components/Inputs/SearchInput";

import { isValidForm, buildPayload } from "../utils/amlForm";
import { makeHandleChange, makeDobChange } from "../utils/factories/factories";
import {
  initialPEPSanctionsState,
  validatePEPSanctions,
} from "../utils/checkStates/pepSanctionsState";

import { useAdminMails as useAdminAdviserMails } from "../../../context/AdminEmailsContext";
import { useUserEmail } from "../../../context/UserEmailContext";

import { addDoc, collection, doc, setDoc, updateDoc } from "firebase/firestore";
import { auth, firestore as dbAML } from "../../../backend/firebaseConfig";
import { getEndUserId } from "../utils/helperFunctions/getEndUserId";
import { useNavigate } from "react-router-dom";
import {
  getAddressAutocomplete,
  getAddressById,
} from "../utils/postcodes/getAddressClient";

const PEPSanctionsCheck = () => {
  const { userEmail } = useUserEmail();
  const { currentAdviserEmail } = useAdminAdviserMails();

  const [formData, setFormData] = useState(initialPEPSanctionsState);
  const [errors, setErrors] = useState({});
  const [resetSeed, setResetSeed] = useState(0);

  const AML_SERVER = process.env.REACT_APP_AML_SERVER;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmissionPopUp, setShowSubmissionPopUp] = useState(false);
  const navigate = useNavigate();
  const handleChange = makeHandleChange(setFormData, setErrors);
  const handleDobChange = makeDobChange(setFormData, setErrors);

  // ---------------- Address search state ----------------
  // UI input value
  const [addressSearch, setAddressSearch] = useState(
    initialPEPSanctionsState?.address?.postcode || ""
  );

  // separate term actually used for API calls (what you asked for)
  const [addressSearchTerm, setAddressSearchTerm] = useState("");

  const [addrState, setAddrState] = useState({ state: "idle", msg: "" });
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [showAddrDropdown, setShowAddrDropdown] = useState(false);

  const addrDebounceRef = useRef(null);
  const autoAbortRef = useRef(null);
  const getAbortRef = useRef(null);

  const cancel = (ref) => {
    try {
      ref.current?.abort?.();
    } catch {}
  };

  // keep SearchInput synced if postcode is updated elsewhere (e.g. selecting address)
  useEffect(() => {
    const pc = formData.address.postcode || "";
    setAddressSearch(pc);

    // keep the "search term" aligned too (optional but usually desirable)
    setAddressSearchTerm(pc);
  }, [formData.address.postcode]);

  const runAutocomplete = async (term) => {
    const t = String(term || "").trim();

    if (t.length < 2) {
      setAddrSuggestions([]);
      setShowAddrDropdown(false);
      setAddrState({ state: "idle", msg: "" });
      return;
    }

    cancel(autoAbortRef);
    const ac = new AbortController();
    autoAbortRef.current = ac;

    setAddrState({ state: "loading", msg: "Searching addresses..." });

    const out = await getAddressAutocomplete(t, { signal: ac.signal });

    if (out?.aborted || ac.signal.aborted) return;
    if (!out.ok) {
      setAddrSuggestions([]);
      setShowAddrDropdown(false);
      setAddrState({ state: "error", msg: out.error || "Autocomplete failed" });
      return;
    }

    const list = out.suggestions || [];
    setAddrSuggestions(list);
    setShowAddrDropdown(list.length > 0);
    setAddrState({
      state: "success",
      msg: list.length ? `Found ${list.length} results` : "No results",
    });
  };

  const applyAddressFromId = async (id) => {
    cancel(getAbortRef);
    const ac = new AbortController();
    getAbortRef.current = ac;

    setAddrState({ state: "loading", msg: "Fetching address..." });

    const out = await getAddressById(id, { signal: ac.signal });

    if (!out.ok) {
      setAddrState({
        state: "error",
        msg: out.error || "Failed to fetch address",
      });
      return;
    }

    const a = out.address || {};

    // Map directly to your form fields
    handleChange("address", "buildingNumber", "text")(a.building_number || "");
    handleChange("address", "buildingName", "text")(a.building_name || "");
    handleChange("address", "street", "text")(a.thoroughfare || a.line_1 || "");
    handleChange("address", "town", "text")(a.town_or_city || "");
    handleChange("address", "county", "text")(a.county || "");

    const nextPc =
      a.postcode || formData.address.postcode || addressSearch || "";

    // update both the form + the UI input + the API term
    handleChange("address", "postcode", "text")(nextPc);
    setAddressSearch(nextPc);
    setAddressSearchTerm(nextPc);

    setShowAddrDropdown(false);
    setAddrSuggestions([]);
    setAddrState({ state: "success", msg: "Address selected" });
  };

  useEffect(() => {
    return () => {
      cancel(autoAbortRef);
      cancel(getAbortRef);
      if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
    };
  }, []);

  const resetAll = () => {
    setFormData(initialPEPSanctionsState);
    setErrors({});
    setResetSeed((n) => n + 1);

    // reset address search UI too
    setAddressSearch("");
    setAddressSearchTerm("");
    setAddrSuggestions([]);
    setShowAddrDropdown(false);
    setAddrState({ state: "idle", msg: "" });
  };

  // ---------------- Submit ----------------
  const handleSubmit = async () => {
    const nextErrors = validatePEPSanctions(formData);
    setErrors(nextErrors);

    if (!isValidForm(nextErrors)) return;
    if (isSubmitting) return;

    const endUserId = getEndUserId(auth.currentUser?.uid);
    const base = (AML_SERVER || "").replace(/\/$/, "");
    if (!base) {
      setErrors((p) => ({ ...p, _server: "AML server URL missing" }));
      return;
    }

    const data = {
      person: {
        firstName: formData.personal.firstName,
        lastName: formData.personal.lastName,
        dob: formData.personal.dob,
      },
      address: {
        buildingNumber: formData.address.buildingNumber || "",
        buildingName: formData.address.buildingName || "",
        street: formData.address.street,
        town: formData.address.town,
        county: formData.address.county,
        postcode: formData.address.postcode,
      },
    };

    let docRef;
    let vendorData;

    try {
      setIsSubmitting(true);

      docRef = await addDoc(
        collection(dbAML, "AML Checks", userEmail, "Checks"),
        {
          checkType: "PEP_Sanctions",
          createdAt: new Date().toISOString(),
          createdBy: userEmail || null,
          forAdviser: currentAdviserEmail || null,
        },
      );

      vendorData = docRef.id;

      setShowSubmissionPopUp(true);

      await updateDoc(docRef, {
        vendorData,
        clientReferenceId: vendorData,
        endUserId: endUserId || "",
        experianStatus: "processing",
        updatedAt: new Date().toISOString(),
      });

      const payloadFb = buildPayload(formData, "PEP_Sanctions");
      await addDoc(collection(docRef, "Request Data"), {
        ...payloadFb,
        endUserId: endUserId || "",
        createdAt: new Date().toISOString(),
      });

      const res = await fetch(`${base}/pep-sanctions-check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          data,
          vendorData,
          endUserId: endUserId || "",
          userEmail: userEmail || "",
        }),
      });

      let json = null;
      if (res.status === 200) json = await res.json();

      if (res.status !== 200) {
        await updateDoc(docRef, {
          experianStatus: "failed",
          errorMessage:
            "Experian returned a non-200 response. The team has been notified.",
          updatedAt: new Date().toISOString(),
        });

        // resetAll();
        return;
      }

      const expRequestId = json?.responseHeader?.expRequestId || null;
      const expDocRef = doc(docRef, "Experian Response", "Response");

      await setDoc(
        expDocRef,
        {
          experianStatus: json?.experianStatus || "completed",
          expRequestId,
          response: json,
          updatedAt: new Date().toISOString(),
          updatedBy: "frontend",
        },
        { merge: true },
      );

      await updateDoc(docRef, {
        experianStatus: "completed",
        expRequestId,
        updatedAt: new Date().toISOString(),
      });

      // resetAll();
    } catch (err) {
      if (docRef) {
        try {
          await updateDoc(docRef, {
            experianStatus: "failed",
            errorMessage: err?.message || "Failed to run PEP & Sanctions",
            updatedAt: new Date().toISOString(),
          });
        } catch {}
      }
    } finally {
      setIsSubmitting(false);
      resetAll();
      // navigate("/my-checks");
    }
  };

  return (
    <div className="Check-Container">
      {/* <Banner
        type="info"
        message="Required when conducting new business for an existing client who has already been verified through an Enhanced or Standard check."
      /> */}

      <div className="Check-Content">
        {/* Personal Information */}
        <div className="Check-Section">
          <div className="Check-Section-Header">
            <PersonalInformationIcon />
            <div className="AML-Text-Input primary">Personal Information</div>
          </div>

          <div className="Check-Section-Inputs">
            <TextInput
              key={`fn-${resetSeed}`}
              label="First Name"
              value={formData.personal.firstName}
              isRequired
              onChange={handleChange("personal", "firstName", "text")}
              placeholder="Alex"
              error={errors?.personal?.firstName}
            />
            <TextInput
              key={`ln-${resetSeed}`}
              label="Last Name"
              value={formData.personal.lastName}
              isRequired
              onChange={handleChange("personal", "lastName", "text")}
              placeholder="Smith"
              error={errors?.personal?.lastName}
            />
            <DateInput
              key={`dob-${resetSeed}`}
              label="Date of Birth"
              value={formData.personal.dob}
              isRequired
              onChange={handleDobChange}
              placeholderText="DD/MM/YYYY"
              dateFormat="DD/MM/YYYY"
              isError={errors?.personal?.dob}
            />
          </div>
        </div>

        <div className="Horizontal-Line" />

        {/* Current Address */}
        <div className="Check-Section">
          <div className="Check-Section-Header">
            <AddressIcon />
            <div className="AML-Text-Input primary">Current Address</div>
          </div>

          <Banner
            type="info"
            message="Please enter a valid UK address, including the building number or name."
          />

          {/* SearchInput + dropdown */}
          <div className="aml-postcode-wrapper">
            <SearchInput
              label="Address Lookup"
              placeholder="Start typing the address or postcode..."
              value={addressSearch}
              onChange={(val) => {
                // UI value
                setAddressSearch(val);

                // keep formData in sync (NO normalisation)
                handleChange("address", "postcode", "text")(val);

                // separate term used for API calls
                setAddressSearchTerm(val);

                // debounce autocomplete
                if (addrDebounceRef.current)
                  clearTimeout(addrDebounceRef.current);
                addrDebounceRef.current = setTimeout(() => {
                  runAutocomplete(val);
                }, 300);
              }}
              onSubmit={() => {
                const term = String(addressSearchTerm || "").trim();
                runAutocomplete(term);
              }}
            />

            {/* <div className="aml-postcode-actions">
              <SecondaryButton
                label={
                  addrState.state === "loading" ? "Searching..." : "Lookup"
                }
                onClick={() => runAutocomplete(addressSearchTerm)}
                isDisabled={addrState.state === "loading"}
              />
            </div> */}

            {showAddrDropdown && addrSuggestions.length > 0 && (
              <div className="aml-postcode-dropdown">
                {addrSuggestions.map((sug) => (
                  <div
                    key={sug.id}
                    className="aml-postcode-option AML-Text-Body"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      applyAddressFromId(sug.id);
                    }}
                  >
                    {sug.address}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Address fields */}
          <div className="Check-Section-Inputs">
            <TextInput
              key={`bnum-${resetSeed}`}
              label="Building Number"
              value={formData.address.buildingNumber}
              onChange={handleChange("address", "buildingNumber", "text")}
              placeholder="123"
              isRequired={false}
              error={errors?.address?.buildingNumber}
              maxLength={10}
            />
            <TextInput
              key={`bname-${resetSeed}`}
              label="Building Name"
              value={formData.address.buildingName}
              onChange={handleChange("address", "buildingName", "text")}
              placeholder="High Tower"
              isRequired={false}
              error={errors?.address?.buildingName}
            />
            <TextInput
              key={`street-${resetSeed}`}
              label="Street"
              value={formData.address.street}
              isRequired
              onChange={handleChange("address", "street", "text")}
              placeholder="High Street"
              error={errors?.address?.street}
            />
          </div>

          <div className="Check-Section-Inputs">
            <TextInput
              key={`town-${resetSeed}`}
              label="Town"
              value={formData.address.town}
              isRequired
              onChange={handleChange("address", "town", "text")}
              placeholder="London"
              error={errors?.address?.town}
            />
            <TextInput
              key={`county-${resetSeed}`}
              label="County"
              value={formData.address.county}
              isRequired
              onChange={handleChange("address", "county", "text")}
              placeholder="Greater London"
              error={errors?.address?.county}
            />
            <TextInput
              label="Postcode"
              value={formData.address.postcode}
              isRequired
              onChange={(e) => {
                // keep as-is (NO normalisation)
                const typed = e.target.value;

                handleChange("address", "postcode", "text")(typed);

                // keep SearchInput UI + API term in sync
                setAddressSearch(typed);
                setAddressSearchTerm(typed);
              }}
              placeholder="AB1 2CD"
              error={errors?.address?.postcode}
            />
          </div>
        </div>
      </div>

      <SecondaryButton
        label={isSubmitting ? "Submitting..." : "Submit"}
        onClick={handleSubmit}
        isDisabled={isSubmitting}
      />

      {showSubmissionPopUp && (
        <SubmissionPopUp
          onClose={() => {
            setShowSubmissionPopUp(false);
            // navigate("/check²");
          }}
        />
      )}
    </div>
  );
};

export default PEPSanctionsCheck;
